import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Card, Form } from '@shopify/polaris';
import { ExportMinor } from '@shopify/polaris-icons';
import PolarisFormProvider from '@container/PolarisFormProvider';

import SectionForm from '@presentational/section/SectionForm';
import FormPageActions from '@container/FormPageActions';
import AppLinksForm from '@presentational/section/AppLinksForm';
import AppLinkModal from '@presentational/section/AppLinkModal';

import { Map, List } from 'immutable'
import { render } from 'react-dom';

const propTypes = {
  submitURL: PropTypes.string.isRequired,
  destroyURL: PropTypes.string,
  record: PropTypes.object,
  fetchCollectionURL: PropTypes.string.isRequired,
  collectionBaseURL: PropTypes.string.isRequired,
  method: PropTypes.string, // HTML form method
  categoryTreeExportURL: PropTypes.string,
}

const defaultProps = {

}

const linkModalActions = { create: "CREATE", update: "UPDATE" };
const defaultLink = {
  title: "", url: "",  position: 1, shopify_collection_id: null, sublinks: [], index: null, class_name: ""
}

const SectionNew = (props) => {
  const { record } = props;
  const [open, setOpen] = useState(false);
  const [hackCounter, setHackCounter] = useState(0); // hack counter to "reset" modal when reopening for new (ONLY WHILE formik is being updated to V2 for hooks)
  const [linkInfo, setLinkInfo] = useState({
    link: defaultLink, // focused link object
    name: "", // might be app_links or app_links[1].sublinks or app_links[1]sublinks[2].sublinks etc
    action: linkModalActions.create,
    deepLevel: 0, // 0 means top level, 1 means 1 level deep, etc
  })

  // Action as action, name as parentCollectionAccessor (i.e. "app_links", "app_links.0.sublinks", etc) and link object when update
  const openLinkModalForm = (action, name, link = null, deepLevel) => {
    if(action == linkModalActions.update){
      setLinkInfo({ ...linkInfo, link: link, action: linkModalActions.update, name, deepLevel })
    }
    else{ // create
      setLinkInfo({ ...linkInfo, link: { ...defaultLink, hackCounter: hackCounter }, action: linkModalActions.create, name, deepLevel })
      setHackCounter(hackCounter+1)
    }
    setOpen(true);
  }

  const closeLinkModalForm = () => {
    setOpen(false);
    setLinkInfo({ ...linkInfo, link: defaultLink, name: "", })
  }

  // From current values of overall form get the current links from provided parentLinkAccessor. Return array of links if found or []
  const getLinksFromParent = (values, parentCollectionAccessor) => {
    const immutableValues = Map(values);
    const parentLinklistPath = parentCollectionAccessor.split(".").map( (fieldKey) => { // TODO IMPROVE lookup of values with name
      return (Number(fieldKey) >= 0) ? Number(fieldKey) : fieldKey; // if it is a string number convert it into number to work with getIn
    } );
    return immutableValues.getIn( parentLinklistPath ) || [];
  };

  const handleLinkRemove = (parentCollectionAccessor, link, renderProps) => {
    const currentLinkslist = List(getLinksFromParent(renderProps.values, parentCollectionAccessor));
    // const newLinks = currentLinkslist.remove(link.index).toJS();
    const newLinks = currentLinkslist.set(link.index, { ...link, _destroy: true }).toJS(); // update on index and add _destroy flag for rails
    renderProps.setFieldValue(parentCollectionAccessor, newLinks);
  }

  const handleLinkSave = (values, renderProps) => {
    const currentLinksOnParent = getLinksFromParent(renderProps.values, linkInfo.name);
    if(linkInfo.action == linkModalActions.update){ // find link and update
      const currentLinkslist = List(currentLinksOnParent);
      const newLinks = currentLinkslist.set(linkInfo.link.index, values).toJS(); // update on index (provided from AppLinkItem) the new values from modal
      renderProps.setFieldValue(linkInfo.name, newLinks);
      closeLinkModalForm();
    }
    else{ // insert new link
      const newLinkPosition = currentLinksOnParent.length + 1;
      const newLinks = [...currentLinksOnParent, { ...values, position: newLinkPosition }];
      renderProps.setFieldValue(linkInfo.name, newLinks);
      closeLinkModalForm();
    }
  }

  // action UP or DOWN
  const handleLinkPositionChange = (parentCollectionAccessor, link, action = "UP", renderProps) => {
    const currentLinkslist = List(getLinksFromParent(renderProps.values, parentCollectionAccessor));
    const delta = action == "UP" ? -1 : 1;
    const newIndexPosition = link.index + delta;
    const newPositionValue = link.position + delta;

    const linkToBeTakenOut = currentLinkslist.get(newIndexPosition); // the link which will exchange places with provided link
    let newLinks = currentLinkslist.set(newIndexPosition, { ...link, position: newPositionValue }); // set link with new index (for re-render) and position when saving to DB
    newLinks = newLinks.set(link.index, { ...linkToBeTakenOut, position: link.position }) // on link's original position set the link that was on new position (up or down) and update with former link position
    renderProps.setFieldValue(parentCollectionAccessor, newLinks.toJS()); // exchange places and update position values.
  }

  // Translate sublinks to sublinks_attributes
  
  const recursiveWriteFormForLinks = (formData, parentName, links) => {
    let index = 0;
    for(let link of links ){
      const formDataLinkName = `${parentName}[${index}]`; // i.e. "section[app_links_attributes][0] / section[app_links_attributes][0][sublinks][0]
      writeSimpleFieldsOnFormData(formData, formDataLinkName, link)
      if(link.sublinks){
        recursiveWriteFormForLinks(formData, `${formDataLinkName}[sublinks_attributes]`, link.sublinks) // Write for children
      }
      index += 1;
    }
  }

  // TODO omit sublinks and app_links
  const writeSimpleFieldsOnFormData = (formData, parentName, object) => {
    for(let fieldName of Object.keys(object)){ // write link attributes
      if(object[fieldName] != null){ // Do not write "nulls"
        if(fieldName == "imageUpload"){
          formData.append(`${parentName}[image]`, object[fieldName], object[fieldName].name);
        }
        else{
          formData.append(`${parentName}[${fieldName}]`, object[fieldName]) // i.e. "section[app_links_attributes][0][title]"
        }
      }
    }
  }
  
  const buildValuesForSubmit = (values, formikBag) => {
    let formData = new FormData();
    // TODO make a super test out of this
    // const sampleData = { title: "Sample title", identifier: values.identifier, app_links: [
    //   { title: "Level 1 (1)", url: "https://foo.com", sublinks: [{ title: "Level 2 (1)", url: "https://unimart.com", sublinks: [{ title: "Level 3", url: "https://unimart.com" }] }, { title: "Level 2 (2)", url: "https://unimart.com" } ] },
    //   { title: "Level 1 (2)", url: "https://foo.com", sublinks: [{ title: "Level 2 (1)", url: "https://unimart.com", sublinks: [{ title: "Level 3", url: "https://unimart.com" }] }, { title: "Level 2 (2)", url: "https://unimart.com" } ] },
    //   { title: "Level 1 (3)", url: "https://foo.com", sublinks: [{ title: "Level 2 (1)", url: "https://unimart.com", sublinks: [{ title: "Level 3", url: "https://unimart.com" }] }, { title: "Level 2 (2)", url: "https://unimart.com" } ] },
    // ]} 

    formData.append("section[title]", "");// to validate if everything is empty
    writeSimpleFieldsOnFormData(formData, "section", values)
    if(values.app_links){
      recursiveWriteFormForLinks(formData, "section[app_links_attributes]", values.app_links); // Translate sublinks into sublinks_attribute
    }

    return formData;
  }

  let secondaryActions = [];
  if(props.categoryTreeExportURL){
    secondaryActions = [
      { content: "Exportar Categorías", url: props.categoryTreeExportURL, external: true, icon: ExportMinor, },
      ...secondaryActions,
    ]
  }

  return (
    <PolarisFormProvider
      title={ I18n.t("activerecord.models.section.one") }
      defaultValues={ record }
      submitURL={ props.submitURL }
      submitMethod={  props.method || "POST" }
      pickValuesCallback={ buildValuesForSubmit }
      pageProps={{
        breadcrumbs: [{content: I18n.t("actions.back"), url: '/sections?resource=links'}],
        secondaryActions: secondaryActions
      }}
    >
      { (renderProps) => 
        <div>
          {/* { console.log("Record", props.record) } */}
          {/* { console.log("Overall form", renderProps.values) } */}
          <Card sectioned>
            <SectionForm errors={ renderProps.errors } values={ renderProps.values } handleChange={ renderProps.handleChange }  />
          </Card>

          <Card title={ I18n.t("activerecord.models.app_link.other") }>
            <AppLinksForm 
              links={ renderProps.values.app_links || [] }
              handleLinkAction={ openLinkModalForm }
              parentListAccessor="app_links"
              handleLinkRemove={ (name, link) => handleLinkRemove(name, link, renderProps) }
              handleLinkPositionChange={ (name, link, positionAction) => handleLinkPositionChange(name, link, positionAction, renderProps) }
            />
          </Card>

          {/* TODO when useFormik gets available PolarisFormProvider can become something more flexible. */}
          <AppLinkModal
            open={ open }
            onClose={ closeLinkModalForm }
            handleSubmit={ (values) => handleLinkSave(values, renderProps) }
            title={ linkInfo.action == linkModalActions.update ? "Actualizar link" : "Agregar link" }
            link={ linkInfo.link }
            ctaTitle={ linkInfo.action == linkModalActions.update ? I18n.t("actions.update") : I18n.t("actions.add") }
            fetchCollectionURL={ props.fetchCollectionURL }
            collectionBaseURL={ props.collectionBaseURL }
            deepLevel={ linkInfo.deepLevel }
          />

          <FormPageActions
            onPrimaryAction={ renderProps.handleSubmit }
            deleteURL={ props.destroyURL }
            loading={ renderProps.loading }
          />
        </div>
      }
    </PolarisFormProvider>
  )
};

SectionNew.propTypes = propTypes;
SectionNew.defaultProps = defaultProps;

export default SectionNew;